import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import tokml from 'geojson-to-kml';
import { cloneDeep, isEmpty } from 'lodash';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, finalize, mergeMap, take } from 'rxjs/operators';

import { AppConfigModel, AppConfigService } from 'src/app/core/api/app-config.service';
import { SpinnerService } from 'src/app/shared/spinner/spinner.service';

import { BuyerService } from '../../../../core/api/buyer.service';
import { DocumentService } from '../../../../core/api/document.service';
import { SiteService } from '../../../../core/api/site.service';
import { FormatHelperService } from '../../../../core/helper/format-helper.service';
import { SiteHelperService } from '../../../../core/helper/site.service';
import { LegalConstants } from '../../../../shared/constants/legal.constants';
import { DialogConfirmSubmitComponent } from '../../../../shared/dialogs/dialog-confirm-submit/dialog-confirm-submit.component';
import { DialogYesNoComponent } from '../../../../shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { Document, Site, User } from '../../../../shared/models';
import * as fromApp from '../../../../store';
import { selectActAsUser } from '../../../../store/auth/auth.selectors';
import { selectSelectedStands } from '../../../../store/parcel/parcel.selectors';
import { clearAllSitesAndReturnToDash, submitSiteFailure, submitSiteSuccess, updateSiteStep, } from '../../../../store/site/site.actions';
import { selectActiveSite } from '../../../../store/site/site.selectors';

@Component({
  selector: 'fc-submit-site',
  templateUrl: './submit-site-page.component.html',
  styleUrls: ['./submit-site-page.component.scss'],
})
export class SubmitSiteComponent implements OnInit {
  @ViewChild('documentUpload', { static: false }) documentUpload: ElementRef;
  @ViewChild('container') container: ElementRef;
  destroyRef = inject(DestroyRef);

  submitForm: UntypedFormGroup;

  actAsUser: User = null;
  activeSite: Site = null;
  selectedStandList: any = [];
  valuePerYear: string;
  area = 0;
  siteId?: number;

  twentyYearMark: string = null;
  twentyYearProjection = 'N/A';
  appConfigTimeToReview: string = '';

  getOpenAssetURL = BuyerService.getOpenAssetURL;

  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly router: Router,
    readonly route: ActivatedRoute,
    readonly documentService: DocumentService,
    private dialog: MatDialog,
    readonly formatHelper: FormatHelperService,
    readonly siteHelper: SiteHelperService,
    readonly spinnerService: SpinnerService,
    private formBuilder: UntypedFormBuilder,
    readonly siteService: SiteService,
    readonly appConfigService: AppConfigService
  ) {
    this.siteId = +this.route.snapshot.params.id;
  }

  ngOnInit() {
    this.submitForm = this.formBuilder.group({
      legalAck: [false, Validators.requiredTrue],
    });
    this.store
      .select(selectActiveSite)
      .pipe(
        take(1),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(activeSite => {
        if (!activeSite) {
          this.router.navigate(['site', this.siteId, 'map-site']);
        } else {
          this.activeSite = activeSite;
          this.store.dispatch(
            updateSiteStep({
              id: this.activeSite.id,
              step: 'Submit',
            })
          );
          this.area = Math.round(this.activeSite.area);
          this.valuePerYear = this.formatHelper.formatMinMaxValuation(
            this.activeSite.getAverageAnnualRevenue(this.activeSite.cohort.id)
          );
          this.twentyYearMark = this.activeSite.getYearMarkString(this.activeSite.cohortId);
          if (!isEmpty(this.activeSite.valuation)) {
            this.twentyYearProjection = this.formatHelper.formatMinMaxValuation(
              this.activeSite.getTwentyYearProjection(this.activeSite.cohort.id)
            );
          }
        }
      });

    combineLatest([this.store.select(selectSelectedStands), this.store.select(selectActAsUser)])
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(storeData => {
        const stands = storeData[0];
        this.actAsUser = storeData[1];
        this.selectedStandList = stands;
        window.scrollTo(0, 0);
      });

    this.appConfigService.getAllAppConfig().subscribe((appConfig: AppConfigModel[]) => {
      this.appConfigTimeToReview = appConfig.find(config => config.key === 'time_to_review').value;
    });
  }

  submitSite(): void {
    this.dialog
      .open(DialogConfirmSubmitComponent)
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.store.dispatch(
            updateSiteStep({
              id: this.activeSite.id,
              step: 'Submitted',
            })
          );
          this.spinnerService.show('app-spinner');

          this.siteService
            .submitSite(this.activeSite.id, this.activeSite.cohort.id)
            .pipe(finalize(() => this.spinnerService.hide('app-spinner')))
            .subscribe(
              site => {
                // Generate and upload KML File
                // Document and Upload
                const arrayOfFeatures = [];

                this.activeSite.versions.forEach(version => {
                  return version.stands.forEach(stand => {
                    const clonedStand = cloneDeep(stand);
                    clonedStand.feature.valuation = null;
                    clonedStand.feature.parcel_id = null;
                    arrayOfFeatures.push(clonedStand.feature);
                  });
                });

                const geoJSONFile = {
                  type: 'FeatureCollection',
                  features: arrayOfFeatures,
                };
                const kml = tokml(geoJSONFile);

                this.documentService
                  .uploadFile(this.activeSite.identifier, kml.toString())
                  .pipe(
                    take(1),
                    mergeMap(fileKey => {
                      const newDoc = new Document({
                        siteId: this.activeSite.id,
                        documentTypeId: this.documentService.getDocTypeId('Site KML'),
                        name: `${new Date().getTime()}_definition.kml`,
                        identifier: fileKey,
                        userId: this.actAsUser.id,
                      });

                      return this.documentService.createDocument(newDoc);
                    }),
                    finalize(() => this.spinnerService.hide('app-spinner'))
                  )
                  .subscribe(
                    document => {
                      this.store.dispatch(submitSiteSuccess({ data: site }));
                      this.spinnerService.hide('app-spinner');
                      this.dialog
                        .open(DialogYesNoComponent, {
                          panelClass: 'fc-yes-no-dialog',
                          data: {
                            title: 'Thank You!',
                            line1:
                              'Your Carbon Offset Site has been submitted to CORE Carbon for review.' +
                              this.activeSite.cohort.docusignTemplateId && !this.activeSite.eDocFulfilled
                                ? 'An email has been sent with a link to electronic documents that must be completed before your site can be reviewed. '
                                : 'We will email you as soon as your map is approved and your contract is ready to be signed.',
                            buttonTextYes: 'Visit My Dashboard',
                          },
                          autoFocus: false,
                        })
                        .afterClosed()
                        .subscribe(() => this.store.dispatch(clearAllSitesAndReturnToDash()));
                    },
                    error => {
                      this.store.dispatch(submitSiteFailure({ error }));
                    }
                  );
              },
              error => {
                this.store.dispatch(submitSiteFailure({ error }));
              }
            );
        }
      });
  }

  projectionInfoBox(year: string): string {
    if (this.activeSite?.cohort?.region) {
      return this.activeSite.cohort.region.projectionInfoText(year, this.activeSite.cohort.initialFixedYears < 20);
    }
    return '';
  }

  get paymentFrequency(): string {
    switch (this.activeSite.cohort.payPeriodCadence) {
      case 12:
        return 'Annually';
      case 6:
        return 'Semi-Annually';
      case 3:
        return 'Quarterly';
      case 1:
        return 'Monthly';
      default:
        return 'Unknown';
    }
  }

  protected readonly LegalConstants = LegalConstants;
}
